import type { IProductBundleOption } from "@rogue/universal"
import type { BundleUIOption } from "../RuleTypes"
import type { Product, IRogue, BuyRequest } from "@/js/services/RogueTypes"
import { findBundleProduct } from "@/js/pages/product/rackbuilder/utils/rackbuilder-util"
import { getOptionsForRackBundle } from "./rack-rules"
import { SLINGER_X_BUNDLE_SKUS } from "@/js/pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXConstants"

// Slinger X shares the Rhino's weight stack clearance constraint - 4 post racks are always limited to 24" depth.
export function getSlingerXGrayedOutValues(rackSize: string | undefined, bundleSku: string | undefined): { rack_depth?: string[] } {
  if (rackSize?.toString()?.[0] !== '4') {
    return {}
  }

  if (!SLINGER_X_BUNDLE_SKUS.includes(bundleSku ?? '')) {
    return {}
  }

  return { rack_depth: ['30"', '43"'] }
}

// Hides/forces options on the main bundle (functional trainer bundle) for Slinger X racks.
// Mirrors getSideOptionsForSlingerX, but since these options can't be hidden as a whole row here,
// disallowed selections are forced down to just the "None" option instead.
export function getOptionsForSlingerX(rogue: IRogue, rack: Product, bundleOption: IProductBundleOption, options: BundleUIOption[], bundleBuyRequest: BuyRequest) {
  const retval = getOptionsForRackBundle(rogue, rack, bundleOption, options, bundleBuyRequest, true) || []

  const title = bundleOption.title?.toLowerCase() ?? ''
  const isSeatOption = title.includes('lat pulldown seat')
  const isSwivelOption = !isSeatOption && title.includes('swivel')
  const isLowRowOption = title.includes('low row')
  const isLatPulldownAttachmentOption = !isSeatOption && title.includes('lat pulldown')

  const selectedSeatName = findBundleProduct(rogue, bundleBuyRequest, 'lat pulldown seat', true)?.name
  const isSeatSelected = !!selectedSeatName
  const isSwivelSelected = !!findBundleProduct(rogue, bundleBuyRequest, 'swivel', true)

  // The plain seat (RM or RML, no kit) and the CTM-1 "Multi-Use" seat can't support a low row attachment - only the "+ Low Row Kit" seat can.
  const seatSupportsLowRow = !!selectedSeatName?.toLowerCase().includes('low row kit')

  const forceNone = () => retval.filter(opt => !opt.value)

  // Lat pulldown seat and the bottom swivel pulley are mutually exclusive
  if ((isSeatOption && isSwivelSelected) || (isSwivelOption && isSeatSelected)) {
    return forceNone()
  }

  // No pulley base selected yet, or the selected seat doesn't support a low row attachment
  if (isLowRowOption && !seatSupportsLowRow && !isSwivelSelected) {
    return forceNone()
  }

  // Lat pulldown attachment hangs off the front pulley itself — the bottom swivel pulley
  // introduces its own top attachment instead, but the seat doesn't affect this pulley
  if (isLatPulldownAttachmentOption && isSwivelSelected) {
    return forceNone()
  }

  return retval
}

// Hides options on the left/right side bundles (SLINGER-X-BUNDLE-LEFT / SLINGER-X-BUNDLE-RIGHT).
// Mirrors getSideOptionsForTheCave: returns false to hide the row entirely, a filtered array, or
// undefined to fall through to normal getBundleOptions processing.
export function getSideOptionsForSlingerX(
  rogue: IRogue,
  title: string,
  sideBuyRequest: BuyRequest,
  options: BundleUIOption[]
): BundleUIOption[] | false | undefined {
  const isSeatOption = title.includes('lat pulldown seat')
  const isSwivelOption = !isSeatOption && title.includes('swivel')
  const isLowRowOption = title.includes('low row')
  const isLatPulldownAttachmentOption = !isSeatOption && title.includes('lat pulldown')

  const selectedSeatName = findBundleProduct(rogue, sideBuyRequest, 'lat pulldown seat', true)?.name
  const isSeatSelected = !!selectedSeatName
  const isSwivelSelected = !!findBundleProduct(rogue, sideBuyRequest, 'swivel', true)

  // The plain seat (RM or RML, no kit) and the CTM-1 "Multi-Use" seat can't support a low row attachment - only the "+ Low Row Kit" seat can.
  const seatSupportsLowRow = !!selectedSeatName?.toLowerCase().includes('low row kit')

  // Lat pulldown seat and the bottom swivel pulley are mutually exclusive
  if ((isSeatOption && isSwivelSelected) || (isSwivelOption && isSeatSelected)) {
    return false
  }

  // No pulley base selected yet, or the selected seat doesn't support a low row attachment
  if (isLowRowOption && !seatSupportsLowRow && !isSwivelSelected) {
    return false
  }

  // Lat pulldown attachment hangs off the front pulley itself — the bottom swivel pulley
  // introduces its own top attachment instead, but the seat doesn't affect this pulley
  if (isLatPulldownAttachmentOption && isSwivelSelected) {
    return false
  }

  return undefined
}
